import React, { useState } from 'react';
import { CircularProgress } from '@mui/material';

type ButtonType = 'button' | 'reset' | 'submit';

interface CustomButtonData {
  name?: string;
  className?: string;
  type?: ButtonType;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  content?: React.ReactNode;
  data?: {
    className?: string;
    type?: ButtonType;
    onSubmitCallback?: () => void;
    href?: string;
    openInNewTab?: boolean | string;
    [key: string]: any;
  };
}

interface CustomButtonProps {
  data: CustomButtonData;
}

const CustomButton = React.forwardRef<
  HTMLButtonElement,
  CustomButtonProps
>((props, ref) => {
  const {
    name,
    className,
    data,
    disabled,
    onClick,
    content,
  } = props.data;

  const [busy, setBusy] = useState(false);

  const callCallback = (): unknown => {
    const cb = data?.onSubmitCallback as any;
    if (!cb) return undefined;
    if (typeof cb === 'function') {
      try {
        const resp =
          (data as any)?.submissionResponse ||
          (window as any).formSubmissionResponse ||
          (window as any).submissionResponse ||
          (window as any).data;

        return cb(resp);
      } catch (err) {
        console.warn(
          'Error calling callback function',
          err,
        );
      }
      return undefined;
    }
    if (typeof cb === 'string') {
      const raw = cb.trim();

      const simpleNameMatch = raw.match(
        /^([A-Za-z_$][\w$]*)(?:\s*\(\s*\))?$/,
      );
      if (simpleNameMatch) {
        const name = simpleNameMatch[1];
        try {
          const win = window as any;
          const fn = win[name];
          const resp =
            data?.submissionResponse ||
            win.formSubmissionResponse ||
            win.submissionResponse ||
            win.data;
          if (typeof fn === 'function') {
            try {
              try {
                return fn(resp);
              } catch {
                return fn();
              }
            } catch (err) {
              console.warn(
                'Error calling global callback',
                err,
              );
              return undefined;
            }
          }
        } catch (e) {}
      }

      const looksLikeFunction =
        /function\b|=>|\{[\s\S]*\}/.test(raw);
      if (looksLikeFunction) {
        try {
          // eslint-disable-next-line no-new-func
          const maybeFn = new Function(
            'return (' + raw + ')',
          )();
          const win = window as any;
          const resp =
            data?.submissionResponse ||
            win.formSubmissionResponse ||
            win.submissionResponse ||
            win.data;

          if (typeof maybeFn === 'function') {
            try {
              try {
                return maybeFn(resp);
              } catch {
                return maybeFn();
              }
            } catch (err) {
              return undefined;
            }
          }
        } catch (err) {
          // ignore and fallthrough to executing as body
        }
      }

      try {
        // eslint-disable-next-line no-new-func
        const win = window as any;
        const resp =
          data?.submissionResponse ||
          win.formSubmissionResponse ||
          win.submissionResponse ||
          win.data;
        const f = new Function('data', raw);
        return f(resp);
      } catch (err) {
        return undefined;
      }
    }
    return undefined;
  };

  const handleClick = async (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    if (busy) return;

    const t = (data?.type || '').toString().toLowerCase();
    const hasCallback = Boolean((data as any)?.onSubmitCallback);
    const isNavType =
      t === 'submit' ||
      t === 'gotowebinar' ||
      t === 'next' ||
      t === 'nextstep' ||
      t === 'prev' ||
      t === 'prevstep' ||
      t === 'previous';

    if (hasCallback || isNavType) {
      try { e.preventDefault(); } catch (e) {}
    }

    if (onClick) {
      try {
        onClick(e);
      } catch (err) {
        console.warn('onClick error', err);
      }
    }

    try {
      // The callback (e.g. FormRenderer's handleSubmit) may be async and does
      // its own required-field validation, resolving to `false` when the
      // submit was rejected or blocked. Navigation must wait for it - an href
      // redirect fired synchronously would tear the page down mid-request,
      // skip validation, and interpolate {{id}} placeholders before the
      // response exists.
      const callbackResult = callCallback();

      let actionsResult: unknown;
      const win = window as any;
      const actions = win.__formActions || win.formRendererActions || null;
      // FormRenderer already wires onSubmitCallback to the real handler
      // (handleSubmit/handleNext/handlePrevious/submitCurrentStep) for
      // CustomButton fields. Only fall back to window.__formActions when the
      // callback did nothing, otherwise the submit/next action fires twice.
      if (actions && callbackResult === undefined) {
        if (t === 'next' || t === 'nextstep') {
          try { actionsResult = actions.next && actions.next(); } catch (e) { /* ignore */ }
        } else if (t === 'prev' || t === 'previous' || t === 'prevstep') {
          try { actionsResult = actions.prev && actions.prev(); } catch (e) { /* ignore */ }
        } else if (t === 'submit' || t === 'gotowebinar') {
          try { actionsResult = actions.submit && actions.submit(); } catch (e) { /* ignore */ }
        } else if (t === 'reset') {
          try { actionsResult = actions.reset && actions.reset(); } catch (e) { /* ignore */ }
        }
      }

      const results = [callbackResult, actionsResult].filter(
        (r) => r !== undefined,
      );
      const pending = results.filter(
        (r: any) => r && typeof r.then === 'function',
      );

      if (!pending.length) {
        if (!results.includes(false)) navigate();
        return;
      }

      setBusy(true);
      const settled = await Promise.all(pending as Promise<unknown>[]);

      // Submit/validation handlers (e.g. required-field checks) resolve to
      // `false` when the submit was rejected - stay on the page instead of
      // navigating, but fall through to `finally` so the button unblocks
      // and the user can fix the fields and try again.
      if (settled.includes(false)) return;

      navigate();
    } catch (err) {
      console.warn('Skipping navigation, submission failed', err);
    } finally {
      setBusy(false);
    }
  };

  const navigate = () => {
    try {
      const hrefRaw = data?.href;
      if (hrefRaw && typeof hrefRaw === 'string') {
        const getValueByPath = (obj: any, path: string) => {
          if (!obj || !path) return undefined;
          const parts = path.split('.');
          let cur = obj;
          for (const p of parts) {
            if (cur === undefined || cur === null) return undefined;
            cur = cur[p];
          }
          return cur;
        };

        const replaceTemplatePlaceholdersLocal = (template: string, dataObj: any) => {
          if (!template) return template;
          return template.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_, key: string) => {
            const val = getValueByPath(dataObj, key.trim());
            return val === undefined || val === null ? '' : String(val);
          });
        };

        const resp =
          (data as any)?.submissionResponse ||
          (window as any).formSubmissionResponse ||
          (window as any).submissionResponse ||
          (window as any).data;

        const respData =
          resp && typeof resp.data === 'object' && resp.data !== null
            ? resp.data
            : undefined;
        const inferredId =
          (resp && (resp.id ?? resp._id)) ??
          (respData && (respData.id ?? respData._id));

        const urlParams: Record<string, string> = {};
        try {
          new URLSearchParams(window.location.search).forEach((v, k) => {
            urlParams[k] = v;
          });
        } catch (e) {
          // ignore
        }

        const merged: Record<string, any> = {};
        const assignDefined = (src: any) => {
          if (!src || typeof src !== 'object') return;
          Object.keys(src).forEach((k) => {
            const v = src[k];
            if (v !== undefined && v !== null && v !== '') merged[k] = v;
          });
        };
        assignDefined(urlParams);
        assignDefined(respData);
        assignDefined(resp);
        if (inferredId !== undefined && inferredId !== null) {
          merged.id = inferredId;
        }

        const templateContext = { ...merged, data: { ...merged } };

        let template = (data as any)?.hrefTemplate || hrefRaw;
        try {
          let decoded = template;
          for (let i = 0; i < 4; i++) {
            try {
              const next = decodeURIComponent(decoded);
              if (next === decoded) break;
              decoded = next;
            } catch (e) {
              break;
            }
          }
          if (/\{\{/.test(decoded)) template = decoded;
        } catch (e) {
          // ignore
        }

        let finalHref = template;
        if (/\{\{/.test(template)) {
          try {
            finalHref = replaceTemplatePlaceholdersLocal(template, templateContext);
          } catch (e) {
            // ignore
          }
        }

        const rawOpen = data?.openInNewTab;
        const openInNewTab =
          rawOpen === true ||
          (typeof rawOpen === 'string' && String(rawOpen).toLowerCase() === 'true');

        if (openInNewTab) {
          window.open(String(finalHref), '_blank');
        } else {
          window.location.href = String(finalHref);
        }
      }
    } catch (err) {
      console.warn('Error handling navigation for button href', err);
    }
  };

  const renderContent = () => {
    if (busy) {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <CircularProgress size={14} thickness={5} sx={{ color: 'inherit' }} />
          Submitting...
        </span>
      );
    }
    if (typeof content === 'string') {
      return (
        <span
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    }
    return content as React.ReactNode;
  };

  return (
    <button
      ref={ref}
      name={name}
      type={
        typeof data?.type === 'string'
          ? data.type
          : 'button'
      }
      className={className}
      disabled={disabled || busy}
      onClick={handleClick}
    >
      {renderContent()}
    </button>
  );
});

export default CustomButton;
